import { useEffect } from 'react';
import type { CrmContact } from '../../api/types';
import { useLoad } from '../../lib/useLoad';
import type { CommsSource } from './source';

/**
 * GET /contacts/:id, shared by the thread header and the context panel.
 *
 * Both want the same three things the conversation's embedded contact cannot
 * carry — the presigned avatar url, the dispatch customer snapshot, and the
 * live name — and both mount in the same commit when the panel is open. The
 * in-flight map collapses that into ONE request; it is deliberately dropped as
 * soon as the request settles, so this is a request deduper and not a cache
 * that could serve a stale customer after an agent re-linked one.
 */
const inFlight = new Map<number, Promise<CrmContact>>();

export function useContactDetail(source: CommsSource, contactId: number) {
  const load = useLoad(() => {
    const pending = inFlight.get(contactId);
    if (pending) return pending;
    const started = source.api.getContact(contactId).finally(() => inFlight.delete(contactId));
    inFlight.set(contactId, started);
    return started;
  }, [source, contactId]);
  // Live: a colleague's rename, or dispatch answering whose number this is,
  // arrives as contact.updated. The header and the panel used to learn of it
  // on the next open of the thread, while the list row beside them already
  // said otherwise. Both mount this hook, and the deduper above folds their
  // two reloads into one request.
  const { reload } = load;
  useEffect(
    () =>
      source.subscribe((event) => {
        if (event.name === 'contact.updated' && (event.payload as CrmContact).id === contactId) reload();
      }),
    [source, contactId, reload],
  );
  // A stale answer here is the WRONG PERSON, not an older copy of the right
  // one: useLoad keeps `data` across a change of inputs so views can decide
  // for themselves, and the header and the panel decided nothing — the last
  // customer's name, number and photo were drawn under the next conversation
  // for the length of a round trip. Nothing is better than that, and every
  // consumer falls back to the row's own contact.
  return load.stale ? { ...load, data: null } : load;
}
